import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import type { SwotCategory, SwotEntry, SwotPayload } from './types';
import { SWOT_CATEGORIES, SWOT_CATEGORY_LABEL } from './swotUtils';
import { computeSwotIndex, SCORE_OPTIONS } from './scoringUtils';

interface SwotFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial?: SwotEntry | null;
  defaultCategory?: SwotCategory;
  onSubmit: (payload: SwotPayload) => Promise<void>;
}

export function SwotFormModal({
  open,
  onOpenChange,
  initial,
  defaultCategory = 'fortaleza',
  onSubmit,
}: SwotFormModalProps) {
  const [category, setCategory] = useState<SwotCategory>(defaultCategory);
  const [description, setDescription] = useState('');
  const [impact, setImpact] = useState('');
  const [severity, setSeverity] = useState<number | ''>('');
  const [occurrence, setOccurrence] = useState<number | ''>('');
  const [control, setControl] = useState<number | ''>('');
  const [guideline, setGuideline] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const liveIndex = useMemo(() => {
    if (severity === '' || occurrence === '' || control === '') return null;
    return computeSwotIndex(severity, occurrence, control);
  }, [severity, occurrence, control]);

  useEffect(() => {
    if (!open) return;
    setCategory(initial?.category ?? defaultCategory);
    setDescription(initial?.description ?? '');
    setImpact(initial?.impact ?? '');
    setSeverity(initial?.severity ?? '');
    setOccurrence(initial?.occurrence ?? '');
    setControl(initial?.control ?? '');
    setGuideline(initial?.guideline ?? '');
  }, [open, initial, defaultCategory]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onSubmit({
        category,
        description: description.trim(),
        impact: impact.trim() || null,
        severity: severity === '' ? null : severity,
        occurrence: occurrence === '' ? null : occurrence,
        control: control === '' ? null : control,
        guideline: guideline.trim() || null,
      });
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-lg max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>{initial ? 'Editar entrada FODA' : 'Nueva entrada FODA'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className='space-y-4'>
          <div>
            <label htmlFor='swot-category' className='block text-sm font-medium mb-1'>
              Categoría
            </label>
            <select
              id='swot-category'
              value={category}
              onChange={(e) => setCategory(e.target.value as SwotCategory)}
              className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
            >
              {SWOT_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {SWOT_CATEGORY_LABEL[cat]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor='swot-description' className='block text-sm font-medium mb-1'>
              Descripción
            </label>
            <textarea
              id='swot-description'
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              rows={4}
              placeholder='Describa la fortaleza, debilidad, oportunidad o amenaza…'
              className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
            />
          </div>
          <div>
            <label htmlFor='swot-impact' className='block text-sm font-medium mb-1'>
              Impacto (opcional)
            </label>
            <input
              id='swot-impact'
              type='text'
              value={impact}
              onChange={(e) => setImpact(e.target.value)}
              maxLength={500}
              placeholder='Ej: Alto impacto en costos'
              className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
            />
          </div>
          <fieldset className='rounded-md border border-border p-3 space-y-3'>
            <legend className='text-sm font-medium px-1'>Evaluación S × O × D</legend>
            <div className='grid grid-cols-3 gap-3'>
              <div>
                <label htmlFor='swot-severity' className='block text-xs font-medium mb-1'>
                  Severidad (S)
                </label>
                <select
                  id='swot-severity'
                  value={severity}
                  onChange={(e) =>
                    setSeverity(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  className='w-full rounded-md border border-input bg-background px-2 py-2 text-sm'
                >
                  <option value=''>—</option>
                  {SCORE_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor='swot-occurrence' className='block text-xs font-medium mb-1'>
                  Ocurrencia (O)
                </label>
                <select
                  id='swot-occurrence'
                  value={occurrence}
                  onChange={(e) =>
                    setOccurrence(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  className='w-full rounded-md border border-input bg-background px-2 py-2 text-sm'
                >
                  <option value=''>—</option>
                  {SCORE_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor='swot-control' className='block text-xs font-medium mb-1'>
                  Detección (D)
                </label>
                <select
                  id='swot-control'
                  value={control}
                  onChange={(e) => setControl(e.target.value === '' ? '' : Number(e.target.value))}
                  className='w-full rounded-md border border-input bg-background px-2 py-2 text-sm'
                >
                  <option value=''>—</option>
                  {SCORE_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {liveIndex !== null && (
              <p className='text-sm text-muted-foreground'>
                Índice de prioridad: <strong>{liveIndex}</strong>
              </p>
            )}
          </fieldset>
          <div>
            <label htmlFor='swot-guideline' className='block text-sm font-medium mb-1'>
              Pauta / acción (opcional)
            </label>
            <textarea
              id='swot-guideline'
              value={guideline}
              onChange={(e) => setGuideline(e.target.value)}
              rows={2}
              placeholder='Pauta de respuesta o seguimiento…'
              className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
            />
          </div>
          <div className='flex justify-end gap-2'>
            <button
              type='button'
              onClick={() => onOpenChange(false)}
              className='px-4 py-2 text-sm rounded-md border border-border hover:bg-muted'
            >
              Cancelar
            </button>
            <button
              type='submit'
              disabled={submitting || !description.trim()}
              className='px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground disabled:opacity-50'
            >
              {submitting ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
